import { BrushBehavior, brushX, D3BrushEvent, ScaleLinear, select } from 'd3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { FLIP_HANDLE_THRESHOLD_PX, BRUSH_EXTENT_MARGIN_PX } from './BrushStyles';
import { compareExtents } from './BrushUtils';
import { BrushHandles } from './BrushHandle';

type BrushSliderProps = {
  id: string;
  xScale: ScaleLinear<number, number>;
  interactive: boolean;
  brushLabelValue: (d: 'left' | 'right', x: number) => string;
  brushExtent: [number, number];
  setBrushExtent: (extent: [number, number], mode: string | undefined) => void;
  innerWidth: number;
  innerHeight: number;
  leftHandleColor: string;
  rightHandleColor: string;
  isFullRange: boolean;
};

export function BrushSlider({
  id,
  xScale,
  interactive,
  brushLabelValue,
  brushExtent,
  setBrushExtent,
  innerWidth,
  innerHeight,
  leftHandleColor,
  rightHandleColor,
  isFullRange,
}: BrushSliderProps) {
  const brushRef = useRef<SVGGElement | null>(null);
  const brushBehavior = useRef<BrushBehavior<SVGGElement> | null>(null);

  // State
  const [localBrushExtent, setLocalBrushExtent] = useState<[number, number]>(brushExtent);
  const [hovering, setHovering] = useState(false);

  // Update brush selection when brushExtent changes
  useEffect(() => {
    if (!brushRef.current || !brushBehavior.current) return;

    const brushEl = select(brushRef.current);
    const selection = brushExtent.map(xScale) as [number, number];

    // Update d3 brush selection
    brushEl.call(brushBehavior.current.move, selection);
    setLocalBrushExtent(brushExtent);
  }, [brushExtent, xScale]);

  // Initialize brush
  useEffect(() => {
    if (!brushRef.current) return;

    const currentBrushRef = brushRef.current;
    const brushEl = select(currentBrushRef);

    // Clear existing brush
    brushEl.selectAll('*').remove();

    brushBehavior.current = brushX<SVGGElement>()
      .extent([
        [Math.max(0 + BRUSH_EXTENT_MARGIN_PX, xScale(0)), 0],
        [innerWidth - BRUSH_EXTENT_MARGIN_PX, innerHeight],
      ])
      .handleSize(40)
      .filter(() => interactive)
      .on('brush end', (event: D3BrushEvent<unknown>) => {
        const { type, selection, mode } = event;

        if (!selection) {
          return;
        }

        const scaled = (selection as [number, number]).map(xScale.invert) as [number, number];

        if (type === 'end' && !compareExtents(brushExtent, scaled, xScale)) {
          setBrushExtent(scaled, mode);
        }

        setLocalBrushExtent(scaled);
      });

    // Initialize brush
    brushBehavior.current(brushEl);

    // Set initial selection
    const selection = brushExtent.map(xScale) as [number, number];
    brushEl.call(brushBehavior.current.move, selection);

    // Update brush selection styles - light blue semi-transparent fill per Figma
    // No stroke here - we render custom dashed lines for left/right borders only
    brushEl
      .select('.selection')
      .attr('stroke', 'none')
      .attr('fill', '#f5f8ff')
      .attr('fill-opacity', '0.7');

    // Keep d3 brush handles active for resizing, but make them transparent
    // Our custom BrushHandle components are visual overlays
    brushEl.selectAll('.handle').attr('fill', 'transparent');

    return () => {
      brushEl.selectAll('*').remove();
    };
  }, [id, innerHeight, innerWidth, interactive, xScale, brushExtent, setBrushExtent]);

  // Handle visibility states
  const visibility = useMemo(() => {
    if (!localBrushExtent) return null;

    return {
      flipLeftHandle: xScale(localBrushExtent[0]) > FLIP_HANDLE_THRESHOLD_PX,
      flipRightHandle: xScale(localBrushExtent[1]) > innerWidth - FLIP_HANDLE_THRESHOLD_PX,
      showLeftArrow:
        isFullRange || xScale(localBrushExtent[0]) < 0 || xScale(localBrushExtent[1]) < 0,
      showRightArrow:
        isFullRange ||
        xScale(localBrushExtent[0]) > innerWidth ||
        xScale(localBrushExtent[1]) > innerWidth,
      leftHandleInView:
        !isFullRange &&
        xScale(localBrushExtent[0]) >= 0 &&
        xScale(localBrushExtent[0]) <= innerWidth,
      rightHandleInView:
        !isFullRange &&
        xScale(localBrushExtent[1]) >= 0 &&
        xScale(localBrushExtent[1]) <= innerWidth,
    };
  }, [localBrushExtent, xScale, innerWidth, isFullRange]);

  // Event handlers
  const handleMouseEnter = useCallback(() => setHovering(true), []);
  const handleMouseLeave = useCallback(() => setHovering(false), []);
  const handleMouseDown = useCallback((e: React.MouseEvent) => e.preventDefault(), []);

  if (!visibility) return null;

  return (
    <>
      <defs>
        <linearGradient id={`${id}-selection`} x1="0%" y1="100%" x2="100%" y2="100%">
          <stop stopColor={leftHandleColor} />
          <stop stopColor={rightHandleColor} offset="1" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <rect x="0" y="0" width={innerWidth} height={innerHeight} />
        </clipPath>
      </defs>

      <g
        ref={brushRef}
        clipPath={`url(#${id}-clip)`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
      >
        <rect
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          pointerEvents={interactive ? 'all' : 'none'}
        />
      </g>

      {localBrushExtent && (
        <>
          {/* Dashed vertical borders - always show */}
          <line
            x1={xScale(localBrushExtent[0])}
            y1={0}
            x2={xScale(localBrushExtent[0])}
            y2={innerHeight}
            stroke={leftHandleColor}
            strokeWidth={2}
            strokeDasharray="4,4"
            pointerEvents="none"
          />
          <line
            x1={xScale(localBrushExtent[1])}
            y1={0}
            x2={xScale(localBrushExtent[1])}
            y2={innerHeight}
            stroke={rightHandleColor}
            strokeWidth={2}
            strokeDasharray="4,4"
            pointerEvents="none"
          />

          {/* Only show brush handles when interactive */}
          {interactive && (
            <BrushHandles
              visibility={visibility}
              localBrushExtent={localBrushExtent}
              xScale={xScale}
              innerHeight={innerHeight}
              innerWidth={innerWidth}
              showLabels={hovering}
              hovering={hovering}
              brushLabelValue={brushLabelValue}
              leftHandleColor={leftHandleColor}
              rightHandleColor={rightHandleColor}
            />
          )}
        </>
      )}
    </>
  );
}
